// COPIED from wac-uniform-publisher src/lib/wae-connector/reshape.ts — keep in sync.
// The only difference is bracket property access, e.g. `file['url']` instead of
// `file.url`, because wac-web sets `noPropertyAccessFromIndexSignature` and the
// publisher does not, plus `=== null || === undefined` in place of `== null`, because
// wac-web's eslint enforces eqeqeq. The logic is identical; diff the two before you
// edit either.
/**
 * GraphQL → Contentful-REST reshape.
 *
 * The WAE Contentful connector queries Contentful's **GraphQL** API, which
 * returns a FLAT item shape:
 *
 *   { sys: { id }, templateNameExternal: "…",
 *     categoryCollection: { items: [ { categoryNameExternal: "…" } ] },
 *     templateThumbnail: { url: "…", title: "…" } }
 *
 * But the existing WAC gallery components (`filterableCard`, etc.) and every
 * other consumer of the legacy `queryTemplates` data source bind to the
 * Contentful **CDA REST** entry shape:
 *
 *   { sys: { id }, fields: { templateNameExternal: "…",
 *     category: [ { fields: { categoryNameExternal: "…" } } ],
 *     templateThumbnail: { fields: { file: { url: "…", title: "…" } } } } }
 *
 * Reshaping GraphQL → REST here makes the connector a **drop-in replacement**
 * for `queryTemplates`: existing `$:fields.X` bindings keep working unchanged.
 *
 * ── EDGEHANCER CONTRACT ──────────────────────────────────────────────────
 * This module is dependency-free and intended to be copied VERBATIM into the
 * `wac-management-service` edgehancer (like `edgehancer-query.ts`). The
 * edgehancer should run `reshapeCollection()` on the Contentful GraphQL
 * response BEFORE returning it as the data resource result, so the runtime
 * data matches the REST shape. This repo's editor preview uses the same
 * function so authoring and runtime are identical.
 */

/** A Contentful asset detected in a GraphQL response (`{ url, title, ... }`). */
type GraphQLAsset = {
	url?: string;
	title?: string;
	description?: string;
	width?: number;
	height?: number;
	contentType?: string;
	fileName?: string;
	size?: number;
};

/** A GraphQL collection wrapper (`{ items: [...] }`). */
type GraphQLCollection = { items?: unknown[] };

/**
 * Depth cap for the reshape walk, counted in JSON levels.
 *
 * The query builder already caps a nested selection at `EH_MAX_SELECTION_DEPTH`,
 * and each selection level yields about two JSON levels, so a generated query
 * never reaches this cap. It bounds the output of a raw query override instead.
 *
 * A cycle guard is deliberately absent. The input is a parsed Contentful GraphQL
 * response, and JSON holds no object cycle. A `WeakSet` guard would also rewrite
 * a legitimately shared object into a stub on its second visit.
 */
const MAX_RESHAPE_DEPTH = 12;

const ASSET_KEYS = new Set([
	'url',
	'title',
	'description',
	'width',
	'height',
	'contentType',
	'fileName',
	'size',
]);

/**
 * Heuristic: is this object a Contentful **Asset** as returned by GraphQL?
 * GraphQL assets expose `url` (and usually `title`/`width`/`contentType`) as
 * top-level scalars, with no `sys`-less nested `fields`.
 */
function isGraphQLAsset(value: Record<string, unknown>): boolean {
	if (typeof value['url'] !== 'string') {
		return false;
	}
	// Every own key (besides `sys`) should be a known asset scalar.
	return Object.keys(value).every((k) => k === 'sys' || ASSET_KEYS.has(k));
}

/** Reshape a GraphQL asset → REST asset (`{ fields: { file: { url, ... } } }`). */
function reshapeAsset(asset: GraphQLAsset): Record<string, unknown> {
	const file: Record<string, unknown> = {};
	if (asset.url !== undefined) file['url'] = asset.url;
	if (asset.fileName !== undefined) file['fileName'] = asset.fileName;
	if (asset.contentType !== undefined) file['contentType'] = asset.contentType;
	if (asset.width !== undefined || asset.height !== undefined) {
		file['details'] = {
			image: {
				...(asset.width !== undefined ? { width: asset.width } : {}),
				...(asset.height !== undefined ? { height: asset.height } : {}),
			},
		};
	}
	const fields: Record<string, unknown> = { file };
	if (asset.title !== undefined) {
		fields['title'] = asset.title;
		// The REST CDA keeps `title` beside `file`, but the WAC client mapper
		// (`map-template-item.ts`) puts it INSIDE `file`, and live compositions bind
		// `fields.<asset>.fields.file.title` for image alt text. Write both keys so
		// the connector matches those bindings and the REST shape at the same time.
		file['title'] = asset.title;
	}
	if (asset.description !== undefined) fields['description'] = asset.description;
	return { fields };
}

/**
 * Reshape a single GraphQL field value into its REST equivalent.
 *
 * - Scalars / arrays of scalars: returned as-is.
 * - Assets (`{ url, title }`): → `{ fields: { file: { url, … }, title } }`.
 * - Collections (`{ items: [...] }`): → array of reshaped linked entries.
 * - Single linked entries (nested objects with their own fields): → `{ fields: {…} }`.
 */
function reshapeValue(value: unknown, depth = 0): unknown {
	if (value === null || value === undefined || typeof value !== 'object') {
		return value; // scalar
	}
	// Depth guard: stop reshaping past the cap and return the branch untouched.
	// A nested selection set is capped in the query builder, so this only trips on
	// a hand-written raw query override that nests deeper than the cap.
	if (depth > MAX_RESHAPE_DEPTH) {
		return value;
	}
	if (Array.isArray(value)) {
		return value.map((entry) => reshapeValue(entry, depth + 1));
	}
	const obj = value as Record<string, unknown>;

	// Collection → flatten `items` into an array of reshaped values. Each item
	// goes through `reshapeValue`, not straight to `reshapeLinkedEntry`, so a
	// collection OF ASSETS (e.g. `templatePreviewCollection`) gets the same
	// `{ fields: { file: { url } } }` envelope a single asset field gets. Sending
	// items directly to `reshapeLinkedEntry` skipped the asset branch and produced
	// `fields.templatePreview.0.fields.url`, which no card binding reads.
	if (Array.isArray((obj as GraphQLCollection).items)) {
		return ((obj as GraphQLCollection).items ?? []).map((item) => reshapeValue(item, depth + 1));
	}

	// Asset → REST asset shape.
	if (isGraphQLAsset(obj)) {
		return reshapeAsset(obj as GraphQLAsset);
	}

	// Otherwise it's a single linked entry object.
	return reshapeLinkedEntry(obj, depth);
}

/**
 * Reshape a linked entry: lift its `sys`, and wrap all other (field) keys under
 * `fields`, recursing so nested references/assets are reshaped too.
 */
function reshapeLinkedEntry(entry: unknown, depth = 0): unknown {
	if (entry === null || entry === undefined || typeof entry !== 'object' || Array.isArray(entry)) {
		return entry;
	}
	const obj = entry as Record<string, unknown>;
	const sys = obj['sys'];
	const fields: Record<string, unknown> = {};
	for (const [key, val] of Object.entries(obj)) {
		if (key === 'sys') continue;
		// Reference fields generated by Contentful GraphQL end in `Collection`;
		// strip that suffix so REST bindings use the base field name.
		const restKey = key.endsWith('Collection') ? key.slice(0, -'Collection'.length) : key;
		fields[restKey] = reshapeValue(val, depth + 1);
	}
	return sys === undefined ? { fields } : { sys, fields };
}

/**
 * Reshape one top-level GraphQL item into a Contentful REST entry
 * (`{ sys, fields: { … } }`). The top level is identical to a linked entry.
 */
export function reshapeItem(item: Record<string, unknown>): Record<string, unknown> {
	return reshapeLinkedEntry(item) as Record<string, unknown>;
}

/**
 * Reshape a full list of GraphQL items into REST entries. This is what the
 * edgehancer should apply to `data.<collection>.items` before returning the
 * data resource result.
 */
export function reshapeCollection(
	items: Array<Record<string, unknown>>,
): Array<Record<string, unknown>> {
	return items.map(reshapeItem);
}
